import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Truck, Plus, Trash2, Save, X, RefreshCw, Eye, EyeOff, ArrowUp, ArrowDown, Globe } from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';
import { AutoShippingRules } from './AutoShippingRules';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = 'w-full bg-white dark:bg-[#0f0f10] border border-neutral-200 dark:border-neutral-800 rounded-md px-3 py-2 text-[13.5px] outline-none focus:border-neutral-400';
const smallInput = 'w-full bg-white dark:bg-[#0f0f10] border border-neutral-200 dark:border-neutral-800 rounded-md px-2 py-1.5 text-[12.5px] outline-none focus:border-neutral-400';
const slugify = (s) => (s || '').toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').slice(0, 32);
const empty = { id: '', label: '', description: '', price: '', eta: '', active: true, country_overrides: [] };

const ShippingPanel = () => {
  const { password } = useAdminAuth();
  const headers = useMemo(() => ({ 'X-Admin-Password': password }), [password]);
  const [rows, setRows] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setMsg('');
    try {
      const [so, cs] = await Promise.all([
        axios.get(`${API}/admin/shipping-options`, { headers }),
        axios.get(`${API}/admin/countries`, { headers }),
      ]);
      setRows(so.data);
      setCountries(cs.data);
    } catch (e) { setMsg('Failed to load: ' + (e?.response?.data?.detail || e.message)); }
    finally { setLoading(false); }
  }, [headers]);
  useEffect(() => { load(); }, [load]);

  const upd = (k, v) => setEditing(prev => ({ ...prev, [k]: v }));

  // country_overrides helpers (map keyed by country_id for editor UX, converted to array on save)
  const setOv = (country_id, patch) => setEditing(prev => {
    const list = [...(prev.country_overrides || [])];
    const idx = list.findIndex(o => o.country_id === country_id);
    if (idx >= 0) list[idx] = { ...list[idx], ...patch };
    else list.push({ country_id, price: null, eta: '', is_active: true, ...patch });
    return { ...prev, country_overrides: list };
  });
  const removeOv = (country_id) => setEditing(prev => ({
    ...prev,
    country_overrides: (prev.country_overrides || []).filter(o => o.country_id !== country_id),
  }));
  const getOv = (country_id) => (editing?.country_overrides || []).find(o => o.country_id === country_id) || null;

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      // Serialize overrides — keep only rows the admin actually set (has a price/eta/toggle)
      const cleanOverrides = (editing.country_overrides || [])
        .filter(o => o.country_id)
        .map(o => ({
          country_id: o.country_id,
          price: (o.price === null || o.price === '' || o.price === undefined) ? null : Number(o.price),
          eta: (o.eta || '').trim() || null,
          is_active: o.is_active !== false,
        }))
        // strip rows that have no effect at all
        .filter(o => o.price !== null || o.eta !== null || o.is_active === false);

      const payload = {
        label: editing.label.trim(), description: editing.description || '',
        price: Number(editing.price), eta: editing.eta || '', active: !!editing.active,
        country_overrides: cleanOverrides,
      };
      if (editing.isNew) {
        payload.id = editing.id.trim().toLowerCase();
        if (!payload.id) throw new Error('ID required');
        await axios.post(`${API}/admin/shipping-options`, payload, { headers });
      } else {
        await axios.patch(`${API}/admin/shipping-options/${editing._origId}`, payload, { headers });
      }
      setEditing(null); await load();
    } catch (e) { setMsg('Save failed: ' + (e?.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.label}"?`)) return;
    try { await axios.delete(`${API}/admin/shipping-options/${r.id}`, { headers }); await load(); }
    catch { setMsg('Delete failed'); }
  };
  const toggleActive = async (r) => {
    try { await axios.patch(`${API}/admin/shipping-options/${r.id}`, { active: !r.active }, { headers }); await load(); } catch { setMsg('Toggle failed'); }
  };
  const move = async (r, dir) => {
    const i = rows.findIndex(x => x.id === r.id);
    const other = rows[dir === 'up' ? i - 1 : i + 1];
    if (!other) return;
    try {
      await Promise.all([
        axios.patch(`${API}/admin/shipping-options/${r.id}`, { sort_order: other.sort_order }, { headers }),
        axios.patch(`${API}/admin/shipping-options/${other.id}`, { sort_order: r.sort_order }, { headers }),
      ]);
      await load();
    } catch { setMsg('Reorder failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-[24px] text-neutral-900 dark:text-neutral-100 inline-flex items-center gap-2"><Truck size={20} className="text-neutral-400" /> Shipping options</h2>
          <p className="text-[12.5px] text-neutral-500">These are the shipping options. With Automatic shipping on, checkout picks one for the customer using the rules below; otherwise customers choose. Country overrides set regional prices.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="inline-flex items-center gap-1.5 border border-neutral-200 dark:border-neutral-700 rounded-md px-3 py-2 text-[13px]"><RefreshCw size={13} /> Refresh</button>
          <button data-testid="add-shipping-btn" onClick={() => setEditing({ ...empty, isNew: true })} className="btn-brand inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-[13.5px] font-semibold"><Plus size={14} /> Add option</button>
        </div>
      </div>
      {msg && <div className="text-red-600 text-[13px]">{msg}</div>}
      {!loading && <AutoShippingRules headers={headers} options={rows.filter(r => r.active)} countries={countries} />}

      <div className="rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#141416] overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-4 py-2.5 text-[10.5px] uppercase tracking-widest text-neutral-500 border-b border-neutral-200 dark:border-neutral-800">
          <div className="col-span-1">Order</div><div className="col-span-4">Label</div><div className="col-span-2">ETA</div><div className="col-span-1 text-right">Price</div><div className="col-span-1 text-center">Overrides</div><div className="col-span-1 text-center">Live</div><div className="col-span-2 text-right">Actions</div>
        </div>
        {loading ? <div className="p-6 text-center text-neutral-500 text-[13.5px]">Loading…</div> :
          rows.map((r) => (
            <div key={r.id} data-testid={`shipping-row-${r.id}`} className="grid grid-cols-12 gap-3 px-4 py-3 items-center border-b border-neutral-100 dark:border-neutral-800 last:border-b-0">
              <div className="col-span-1 flex items-center gap-1">
                <span className="text-[12px] text-neutral-500 w-4">{r.sort_order}</span>
                <button onClick={() => move(r, 'up')} className="p-1 rounded hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-500"><ArrowUp size={12} /></button>
                <button onClick={() => move(r, 'down')} className="p-1 rounded hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-500"><ArrowDown size={12} /></button>
              </div>
              <div className="col-span-4">
                <div className="text-[13.5px] text-neutral-900 dark:text-neutral-100 truncate">{r.label}</div>
                <div className="text-[11.5px] text-neutral-500 truncate">{r.description}</div>
              </div>
              <div className="col-span-2 text-[12.5px] text-neutral-600 dark:text-neutral-300">{r.eta || '—'}</div>
              <div className="col-span-1 text-right text-[13.5px] font-medium tabular-nums">${Number(r.price).toFixed(2)}</div>
              <div className="col-span-1 flex justify-center">
                {(r.country_overrides || []).length > 0 ? (
                  <span data-testid={`shipping-overrides-count-${r.id}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium bg-brand-soft text-brand">
                    <Globe size={11} /> {(r.country_overrides || []).length}
                  </span>
                ) : (
                  <span className="text-[11px] text-neutral-400">—</span>
                )}
              </div>
              <div className="col-span-1 flex justify-center">
                <button data-testid={`shipping-toggle-${r.id}`} onClick={() => toggleActive(r)} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium ${r.active ? 'bg-emerald-100 text-emerald-700' : 'bg-neutral-200 text-neutral-600'}`}>
                  {r.active ? <Eye size={11} /> : <EyeOff size={11} />} {r.active ? 'Live' : 'Hidden'}
                </button>
              </div>
              <div className="col-span-2 flex items-center justify-end gap-1">
                <button data-testid={`shipping-edit-${r.id}`} onClick={() => setEditing({ ...r, isNew: false, _origId: r.id, country_overrides: r.country_overrides || [] })} className="px-2 py-1 text-[12px] rounded-md border border-neutral-200 dark:border-neutral-700">Edit</button>
                <button data-testid={`shipping-delete-${r.id}`} onClick={() => remove(r)} className="p-1.5 rounded-md text-neutral-500 hover:text-red-500 hover:bg-neutral-100 dark:hover:bg-neutral-800"><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 backdrop-blur-sm p-4 overflow-y-auto" onClick={() => setEditing(null)}>
          <div data-testid="shipping-form-modal" className="my-10 w-full max-w-2xl bg-white dark:bg-[#141416] rounded-xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200 dark:border-neutral-800">
              <h3 className="font-serif text-[18px] text-neutral-900 dark:text-neutral-100">{editing.isNew ? 'Add shipping option' : 'Edit shipping option'}</h3>
              <button onClick={() => setEditing(null)} className="p-1.5 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
              <label className="block"><span className="text-[11px] tracking-widest uppercase text-neutral-500">ID (slug)</span>
                <input data-testid="shipping-id-input" className={inputCls + (editing.isNew ? '' : ' opacity-60 cursor-not-allowed')} disabled={!editing.isNew} value={editing.id} onChange={(e) => upd('id', slugify(e.target.value))} placeholder="e.g. same-day" />
              </label>
              <label className="block"><span className="text-[11px] tracking-widest uppercase text-neutral-500">Label</span>
                <input data-testid="shipping-label-input" className={inputCls} value={editing.label} onChange={(e) => upd('label', e.target.value)} placeholder="e.g. Same-Day Delivery" />
              </label>
              <label className="block"><span className="text-[11px] tracking-widest uppercase text-neutral-500">Description</span>
                <input className={inputCls} value={editing.description} onChange={(e) => upd('description', e.target.value)} placeholder="Short customer-facing detail" />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block"><span className="text-[11px] tracking-widest uppercase text-neutral-500">Base price ($)</span>
                  <input data-testid="shipping-price-input" type="number" step="0.01" min="0" className={inputCls} value={editing.price} onChange={(e) => upd('price', e.target.value)} />
                </label>
                <label className="block"><span className="text-[11px] tracking-widest uppercase text-neutral-500">Base ETA</span>
                  <input className={inputCls} value={editing.eta} onChange={(e) => upd('eta', e.target.value)} placeholder="e.g. 5-7 business days" />
                </label>
              </div>
              <label className="inline-flex items-center gap-2 text-[13px]">
                <input type="checkbox" checked={!!editing.active} onChange={(e) => upd('active', e.target.checked)} className="w-4 h-4 accent-emerald-600" />
                Active (show at checkout)
              </label>

              {/* Country overrides */}
              <div className="pt-3 border-t border-neutral-200 dark:border-neutral-800" data-testid="country-overrides-section">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="font-serif text-[15px] text-neutral-900 dark:text-neutral-100 inline-flex items-center gap-1.5"><Globe size={14} className="text-neutral-400" /> Country overrides</div>
                    <p className="text-[11.5px] text-neutral-500">Leave blank to use the base rate. Uncheck a row to hide this option for that country.</p>
                  </div>
                </div>
                {countries.length === 0 ? (
                  <p className="text-[12.5px] text-neutral-500 py-3">Add countries in the Countries tab first.</p>
                ) : (
                  <div className="rounded-lg border border-neutral-200 dark:border-neutral-800 overflow-hidden">
                    <div className="grid grid-cols-12 gap-2 px-3 py-2 text-[10.5px] uppercase tracking-widest text-neutral-500 bg-neutral-50 dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800">
                      <div className="col-span-4">Country</div>
                      <div className="col-span-3">Price ($)</div>
                      <div className="col-span-3">ETA</div>
                      <div className="col-span-1 text-center">Live</div>
                      <div className="col-span-1"></div>
                    </div>
                    {countries.map(c => {
                      const ov = getOv(c.id);
                      return (
                        <div key={c.id} data-testid={`override-row-${c.id}`} className="grid grid-cols-12 gap-2 px-3 py-2 items-center border-b border-neutral-100 dark:border-neutral-800 last:border-b-0">
                          <div className="col-span-4 flex items-center gap-2 text-[13px]">
                            <span className="text-lg leading-none">{c.flag || '🌐'}</span>
                            <span className="truncate">{c.name}</span>
                            {!c.is_active && <span className="text-[10px] text-neutral-400">(hidden)</span>}
                          </div>
                          <div className="col-span-3">
                            <input
                              data-testid={`override-price-${c.id}`}
                              type="number" step="0.01" min="0"
                              className={smallInput}
                              placeholder={`base $${Number(editing.price || 0).toFixed(2)}`}
                              value={ov?.price ?? ''}
                              onChange={(e) => setOv(c.id, { price: e.target.value === '' ? null : e.target.value })}
                            />
                          </div>
                          <div className="col-span-3">
                            <input
                              data-testid={`override-eta-${c.id}`}
                              className={smallInput}
                              placeholder={editing.eta ? `base ${editing.eta}` : 'base ETA'}
                              value={ov?.eta ?? ''}
                              onChange={(e) => setOv(c.id, { eta: e.target.value })}
                            />
                          </div>
                          <div className="col-span-1 flex justify-center">
                            <input
                              data-testid={`override-active-${c.id}`}
                              type="checkbox"
                              checked={ov ? ov.is_active !== false : true}
                              onChange={(e) => setOv(c.id, { is_active: e.target.checked })}
                              className="w-4 h-4 accent-emerald-600"
                              title="Uncheck to hide this option for this country"
                            />
                          </div>
                          <div className="col-span-1 flex justify-end">
                            {ov && (
                              <button data-testid={`override-clear-${c.id}`} onClick={() => removeOv(c.id)} className="p-1 rounded text-neutral-400 hover:text-red-500" title="Clear override">
                                <X size={13} />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-neutral-200 dark:border-neutral-800">
              <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-md border border-neutral-200 dark:border-neutral-700 text-[13.5px]">Cancel</button>
              <button data-testid="shipping-save-btn" onClick={save} disabled={saving || !editing.label.trim() || (editing.isNew && !editing.id.trim()) || editing.price === '' || Number(editing.price) < 0} className="btn-brand inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-[13.5px] font-semibold disabled:opacity-60"><Save size={14} /> {saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShippingPanel;
